// Showing, filtering, sorting and grouping values, shared by every view.
import type { Field, FieldType, Filter, Links, Person, Approval, Row, Sort, Value, ViewConfig } from "./pApi";

export const TYPE_INFO: Record<FieldType, { label: string; hint: string; group: "Basic" | "Choice" | "People" | "Linked" | "Calculated"; icon: string }> = {
  text: { label: "Text", hint: "A line of text", group: "Basic", icon: "Aa" },
  longtext: { label: "Long text", hint: "Notes, several lines", group: "Basic", icon: "¶" },
  number: { label: "Number", hint: "A quantity or count", group: "Basic", icon: "#" },
  currency: { label: "Currency", hint: "USD", group: "Basic", icon: "$" },
  percent: { label: "Percent", hint: "45 means 45%", group: "Basic", icon: "%" },
  date: { label: "Date", hint: "A day", group: "Basic", icon: "▦" },
  checkbox: { label: "Checkbox", hint: "Yes or no", group: "Basic", icon: "☑" },
  url: { label: "Link to a web page", hint: "https://…", group: "Basic", icon: "↗" },
  email: { label: "Email", hint: "name@company.com", group: "Basic", icon: "@" },
  select: { label: "Choice", hint: "One of a list", group: "Choice", icon: "◉" },
  multiselect: { label: "Choices", hint: "Several of a list", group: "Choice", icon: "☰" },
  person: { label: "Person", hint: "Someone who uses courageMFG", group: "People", icon: "☻" },
  approval: { label: "Approval", hint: "Ask someone to approve each record", group: "People", icon: "✓" },
  link: { label: "Link to another table", hint: "Records in another table, or this one", group: "Linked", icon: "⇄" },
  lookup: { label: "Lookup", hint: "A field of the linked records", group: "Linked", icon: "↳" },
  rollup: { label: "Rollup", hint: "Sum, count, latest… of the linked records", group: "Linked", icon: "Σ" },
  formula: { label: "Formula", hint: "Calculated from other fields", group: "Calculated", icon: "ƒ" },
  autonumber: { label: "Number of the record", hint: "1, 2, 3… with an optional prefix", group: "Calculated", icon: "1→" },
  created: { label: "Created on", hint: "When the record was added", group: "Calculated", icon: "⏱" },
  updated: { label: "Last changed", hint: "When it last changed", group: "Calculated", icon: "⟳" },
};
export const EDITABLE = (f: Field) => !["lookup", "rollup", "formula", "autonumber", "created", "updated", "approval"].includes(f.type);

export const CHOICE_TONES: Record<string, { bg: string; fg: string; dot: string }> = {
  teal: { bg: "#DDF1F4", fg: "#075F6B", dot: "#0A7C8C" },
  blue: { bg: "#E2ECFB", fg: "#0F4C9C", dot: "#2F6FD6" },
  green: { bg: "#E3F3E3", fg: "#0E6B0E", dot: "#2E9E3E" },
  yellow: { bg: "#FBF1CC", fg: "#7A5A00", dot: "#D8A800" },
  orange: { bg: "#FCE7D6", fg: "#8A3F06", dot: "#E07A1F" },
  red: { bg: "#FBE2E4", fg: "#9B1C2B", dot: "#D13B4B" },
  purple: { bg: "#ECE6FA", fg: "#4F2E99", dot: "#7A57D1" },
  gray: { bg: "#ECECEC", fg: "#3D3D3D", dot: "#8A8A8A" },
};
export const toneOf = (f: Field, name: string) => CHOICE_TONES[f.options.choices?.find((c) => c.name === name)?.color ?? "gray"] ?? CHOICE_TONES.gray;

const usd = (d: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: d, maximumFractionDigits: d });
const num = (d: number) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: d });
export const fmtDate = (s: string) => {
  const d = new Date(s.length === 10 ? s + "T00:00:00" : s);
  return Number.isNaN(+d) ? s : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
export const fmtDateTime = (s: string) => { const d = new Date(s); return Number.isNaN(+d) ? s : d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); };

/** Effective type of a computed field's values (formulas say what they produce; lookups take their source's). */
export function shownType(f: Field, fieldsOf?: (tableId: string) => Field[] | undefined, all?: Field[]): FieldType {
  if (f.type === "formula") return f.resultType === "number" ? "number" : f.resultType === "date" ? "date" : f.resultType === "checkbox" ? "checkbox" : "text";
  if (f.type === "rollup") return f.options.fn === "concat" ? "text" : f.options.fn === "earliest" || f.options.fn === "latest" ? "date" : "number";
  if (f.type === "lookup" && all) {
    const link = all.find((x) => x.key === f.options.link);
    const src = link?.options.table && fieldsOf ? fieldsOf(link.options.table)?.find((x) => x.key === f.options.field) : undefined;
    return src ? src.type : "text";
  }
  return f.type;
}

/** A value as plain text: what's copied, searched, filtered on and grouped by. */
export function text(f: Field, v: Value | undefined, links?: Links): string {
  if (v === null || v === undefined || v === "") return "";
  switch (f.type) {
    case "currency": return typeof v === "number" ? usd(f.options.decimals ?? 2).format(v) : String(v);
    case "number": return typeof v === "number" ? num(f.options.decimals ?? 2).format(v) : String(v);
    case "percent": return typeof v === "number" ? `${num(f.options.decimals ?? 1).format(v)}%` : String(v);
    case "date": return typeof v === "string" ? fmtDate(v) : "";
    case "created": case "updated": return typeof v === "string" ? fmtDateTime(v) : "";
    case "checkbox": return v ? "Yes" : "No";
    case "multiselect": return Array.isArray(v) ? v.join(", ") : String(v);
    case "person": return (v as Person).name ?? "";
    case "approval": { const a = v as Approval; return a.status === "pending" ? `Waiting for ${a.approverName ?? "approval"}` : `${a.status === "approved" ? "Approved" : "Rejected"} by ${a.approverName ?? ""}`; }
    case "link": { const ids = Array.isArray(v) ? (v as string[]) : []; const t = f.options.table ? links?.[f.options.table] : undefined; return ids.map((id) => t?.rows.find((r) => r.id === id)?.label ?? "…").join(", "); }
    case "autonumber": return `${f.options.prefix ?? ""}${v}`;
    case "rollup": if (typeof v === "number") return num(2).format(v); if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)) return fmtDate(v); return String(v);
    case "formula": {
      if (f.resultType === "date" && typeof v === "string") return fmtDate(v);
      if (typeof v === "number") return num(2).format(v);
      if (typeof v === "boolean") return v ? "Yes" : "No";
      return String(v);
    }
    case "lookup": return Array.isArray(v) ? v.map((x) => (x && typeof x === "object" ? (x as Person).name ?? "" : typeof x === "string" && /^\d{4}-\d{2}-\d{2}$/.test(x) ? fmtDate(x) : String(x))).join(", ") : String(v);
    default: return String(v);
  }
}

/** For sorting: numbers stay numbers, dates sort as text (ISO), everything else as lower-case text. */
function sortKey(f: Field, v: Value | undefined, links?: Links): string | number | null {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return v;
  if (typeof v === "boolean") return v ? 1 : 0;
  if (f.type === "select") { const i = f.options.choices?.findIndex((c) => c.name === v) ?? -1; return i >= 0 ? i : String(v); }
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) return v;
  return text(f, v, links).toLowerCase();
}

export function sortRows(rows: Row[], sorts: Sort[] | undefined, fields: Field[], links?: Links): Row[] {
  const ss = (sorts ?? []).map((s) => ({ s, f: fields.find((x) => x.key === s.field) })).filter((x): x is { s: Sort; f: Field } => !!x.f);
  if (!ss.length) return rows;
  return [...rows].sort((a, b) => {
    for (const { s, f } of ss) {
      const x = sortKey(f, a.values[f.key], links), y = sortKey(f, b.values[f.key], links);
      if (x === y) continue;
      if (x === null) return 1; if (y === null) return -1; // empty last, either way
      const c = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y), undefined, { numeric: true });
      if (c) return s.dir === "desc" ? -c : c;
    }
    return a.position - b.position;
  });
}

export const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
export const addDays = (iso: string, n: number) => { const d = new Date(iso + "T00:00:00Z"); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
export const daysBetween = (a: string, b: string) => Math.round((Date.parse(b + "T00:00:00Z") - Date.parse(a + "T00:00:00Z")) / 864e5);

/** Filter operators that make sense for a field. */
export function opsFor(f: Field): { op: string; label: string; needs: boolean }[] {
  const empty = [{ op: "empty", label: "is empty", needs: false }, { op: "not_empty", label: "is not empty", needs: false }];
  const t = f.type === "formula" ? (f.resultType ?? "text") : f.type;
  if (["number", "currency", "percent", "autonumber", "rollup"].includes(t)) return [{ op: "=", label: "=", needs: true }, { op: "!=", label: "≠", needs: true }, { op: "<", label: "<", needs: true }, { op: "<=", label: "≤", needs: true }, { op: ">", label: ">", needs: true }, { op: ">=", label: "≥", needs: true }, ...empty];
  if (["date", "created", "updated"].includes(t)) return [{ op: "is", label: "is", needs: true }, { op: "before", label: "is before", needs: true }, { op: "after", label: "is after", needs: true }, { op: "within_past", label: "is in the last … days", needs: true }, { op: "within_next", label: "is in the next … days", needs: true }, ...empty];
  if (t === "checkbox") return [{ op: "is", label: "is", needs: true }];
  if (t === "select") return [{ op: "is", label: "is", needs: true }, { op: "is_not", label: "is not", needs: true }, { op: "any_of", label: "is any of", needs: true }, ...empty];
  if (t === "person") return [{ op: "is", label: "is", needs: true }, { op: "is_not", label: "is not", needs: true }, ...empty];
  if (t === "approval") return [{ op: "is", label: "is", needs: true }, ...empty];
  return [{ op: "contains", label: "contains", needs: true }, { op: "not_contains", label: "does not contain", needs: true }, { op: "is", label: "is", needs: true }, { op: "is_not", label: "is not", needs: true }, ...empty];
}

export function matchFilter(f: Field, v: Value | undefined, flt: Filter, me: string | null, links?: Links): boolean {
  const isEmpty = v === null || v === undefined || v === "" || (Array.isArray(v) && !v.length) || (f.type === "checkbox" && !v);
  if (flt.op === "empty") return isEmpty;
  if (flt.op === "not_empty") return !isEmpty;
  const want = flt.value;
  if (want === undefined || want === null || want === "") return true; // a filter still being set up doesn't hide anything
  const t = f.type === "formula" ? (f.resultType ?? "text") : f.type;
  if (t === "checkbox") return !!v === (want === true || want === "true");
  if (t === "person") { const id = want === "@me" ? me : String(want); const has = !!v && typeof v === "object" && (v as Person).id === id; return flt.op === "is_not" ? !has : has; }
  if (t === "approval") return !!v && (v as Approval).status === want;
  if (t === "select") {
    if (flt.op === "any_of") return Array.isArray(want) ? want.includes(v as string) : v === want;
    return flt.op === "is_not" ? v !== want : v === want;
  }
  if (["number", "currency", "percent", "autonumber", "rollup"].includes(t)) {
    const x = typeof v === "number" ? v : Number(v); const y = Number(want);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
    switch (flt.op) { case "=": return x === y; case "!=": return x !== y; case "<": return x < y; case "<=": return x <= y; case ">": return x > y; case ">=": return x >= y; }
    return true;
  }
  if (["date", "created", "updated"].includes(t)) {
    if (typeof v !== "string") return false;
    const d = v.slice(0, 10), today = todayIso();
    const w = want === "today" ? today : String(want);
    switch (flt.op) {
      case "is": return d === w;
      case "before": return d < w;
      case "after": return d > w;
      case "within_past": return d <= today && d >= addDays(today, -Number(want));
      case "within_next": return d >= today && d <= addDays(today, Number(want));
    }
    return true;
  }
  const s = text(f, v as Value, links).toLowerCase(), w = String(want).toLowerCase();
  switch (flt.op) {
    case "contains": return s.includes(w);
    case "not_contains": return !s.includes(w);
    case "is": return s === w;
    case "is_not": return s !== w;
  }
  return true;
}

export function filterRows(rows: Row[], cfg: ViewConfig, fields: Field[], me: string | null, search: string, links?: Links): Row[] {
  const fl = (cfg.filters ?? []).map((x) => ({ x, f: fields.find((y) => y.key === x.field) })).filter((p): p is { x: Filter; f: Field } => !!p.f);
  const q = search.trim().toLowerCase();
  return rows.filter((r) => {
    if (fl.length) {
      const results = fl.map(({ x, f }) => matchFilter(f, r.values[f.key], x, me, links));
      if (cfg.match === "any" ? !results.some(Boolean) : !results.every(Boolean)) return false;
    }
    if (q && !fields.some((f) => text(f, r.values[f.key], links).toLowerCase().includes(q)) && !String(r.number).includes(q)) return false;
    return true;
  });
}

/** Groups for a grid or board: by choice (in the field's order), person, checkbox or text. */
export function groupKey(f: Field, v: Value | undefined, links?: Links): string {
  if (f.type === "person") return v && typeof v === "object" ? (v as Person).id : "";
  if (f.type === "checkbox") return v ? "Yes" : "No";
  return text(f, v, links);
}
export function groupLabel(f: Field, key: string, rows: Row[]): string {
  if (!key) return f.type === "person" ? "Nobody" : `No ${f.name.toLowerCase()}`;
  if (f.type === "person") { const r = rows.find((x) => (x.values[f.key] as Person | null)?.id === key); return (r?.values[f.key] as Person)?.name ?? key; }
  return key;
}
export function groupOrder(f: Field, keys: string[]): string[] {
  if (f.type === "select") { const order = (f.options.choices ?? []).map((c) => c.name); return [...order.filter((x) => keys.includes(x)), ...keys.filter((k) => k && !order.includes(k)).sort(), ...(keys.includes("") ? [""] : [])]; }
  if (f.type === "checkbox") return ["Yes", "No"].filter((k) => keys.includes(k));
  return [...keys.filter(Boolean).sort((a, b) => a.localeCompare(b)), ...(keys.includes("") ? [""] : [])];
}

/** Values someone pasted or typed, turned into what the API takes (it accepts text for every type). */
export function fromText(f: Field, s: string, links: Links | undefined, people: { id: string; name: string; email: string | null }[]): unknown {
  const t = s.trim();
  if (!t) return null;
  if (f.type === "person") { const p = people.find((x) => x.name.toLowerCase() === t.toLowerCase() || x.email?.toLowerCase() === t.toLowerCase()); return p ? p.id : t; }
  if (f.type === "link") {
    const rows = f.options.table ? links?.[f.options.table]?.rows ?? [] : [];
    return t.split(/\s*,\s*/).map((lbl) => rows.find((r) => r.label.toLowerCase() === lbl.toLowerCase())?.id).filter(Boolean);
  }
  return t;
}
/** A stored value in the shape the API takes back (people by id). */
export function toInput(f: Field, v: Value | undefined): unknown {
  if (v === undefined) return null;
  if (f.type === "person") return v && typeof v === "object" ? (v as Person).id : null;
  return v;
}

export const initials = (name: string | null | undefined) => (name ?? "?").split(/\s+/).map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
export const AREA_NAMES: Record<string, string> = {
  commercial: "CRM", engineering: "PLM", quality: "Quality", supply: "Supply chain", production: "Production", inventory: "Inventory", maintenance: "Maintenance",
  safety: "Safety and EHS", documents: "Documents", training: "Training", improve: "Improvement", requests: "Requests and approvals", people: "People and HR",
  it: "IT", workplace: "Workplace", finance: "Finance", insights: "Insights and AI", admin: "Admin",
};
